// Persistent user-visible signal for the silent schema-introspection
// row cap (plan16 T7 + extension round). Every introspection fetcher across
// all 5 engines calls `executeQueryV2` with a `maxRows` option — 5,000 per
// page on every engine since S7 item 8 paged the last four — so a
// pathologically large schema can't blow up renderer memory. Each engine
// adapter reports a clip via `QueryExecutionResult.stats.truncated`
// (set from `anyTruncated` in `mssqlDriverAdapter.ts` / `mssqlSqlToolsAdapter.ts` /
// `mysqlAdapter.ts` / `oracleAdapter.ts` / `pgsqlAdapter.ts` / `sqliteAdapter.ts`)
// — but every introspection fetcher only ever read `result.resultSets?.[0]?.rows`
// and silently dropped that flag. Users with very large schemas therefore got
// incomplete IntelliSense with zero indication anything was missing.
//
// Recon note (plan16 T7): the individual result set (`QueryResultSet`) does
// NOT carry a `truncated` field — only `QueryExecutionResult.stats.truncated`
// does. That is safe to key off here because every introspection query is a
// single SELECT statement, so the query-level stat and "this one result set
// was truncated" mean the same thing.
//
// Cap constraint (extension round I2; re-cut by S7 item 4). Paging split the
// old single cap into two independent quantities:
//
//   * PER-REQUEST cap (`*_MAX_ROWS`, handed to `options.maxRows`) — MUST stay
//     ≤ the query cache's windowing threshold (`FAST_PATH_MAX_ROWS`, 50,000,
//     in `electron/result-cache/executeQueryWithCache.ts`). Above the
//     threshold the cache windows the result set and strips inline `rows` to
//     `[]` while `stats.truncated` stays false — the fetcher would silently
//     read an EMPTY schema with no signal at all. EVERY engine's fetchers now
//     issue 5,000-row pages, so all five share one cap
//     (`INTROSPECTION_PAGE_MAX_ROWS` in `introspectionPaging.ts`).
//   * TOTAL row budget (`*_ROW_BUDGET`, declared in
//     `introspectionBudgets.ts`) — how many rows a paged fetch will
//     accumulate across ALL of its pages (tables 200k, columns 2M,
//     indexes/FKs/routine params/types 500k). This is a memory bound only; it
//     is deliberately far above the windowing threshold and must never reach
//     `options.maxRows`. Hitting it is what reports truncation here, with the
//     `artefact` that ran out of budget.
//
// Both are pinned by introspectionTruncation.test.ts, which scans this
// directory's `*_MAX_ROWS` / `*_ROW_BUDGET` declarations and every `maxRows:`
// usage — cap constants MUST keep the `_MAX_ROWS` name suffix (the pin fails
// any `maxRows:` usage whose identifier doesn't, which is also what stops a
// budget from being passed as a per-request cap), and the pin's
// declaration-count floor must be bumped when caps are added or removed.
//
// S7 item 5 ("honest truncation signal") replaced the original one-time
// 5-second toast with a PERSISTENT surface: this module is now the record of
// which artefacts were clipped per `(connectionId, database)`, and the
// IntelliSense pill reads it through `resolveSchemaReadyStatus` — every
// would-be `ready` flip becomes `partial` while a clip stands, with the
// clipped artefacts named in the tooltip and a "Reload schema" action next to
// them (`IntelliSenseStatusIndicator.tsx`). A toast that disappears after 5
// seconds could not answer "why is this completion missing?" ten minutes
// later; the pill can. The low-cardinality analytics counter stays (now with
// an `artefact` property) so we can size how many users actually hit a cap.
//
// Lifetime of a record: it is dropped when the schema bus invalidates the
// connection (post-DDL `invalidateSchema` / disconnect `invalidateConnection`
// — subscribed lazily below, per connection that actually tripped) and when
// `clearTruncation` is called explicitly, which is what the pill's "Reload
// schema" action does BEFORE forcing a refresh. That ordering is what keeps a
// full re-load honest: the refetch re-records only the artefacts that trip
// THIS time, so a database that no longer exceeds the cap returns the pill to
// `ready` on its own.

import { subscribe as subscribeSchemaBus } from '@/features/intellisense/schemaBus/schemaBus';

/** Engines that run schema introspection through this module. */
export type IntrospectionTruncationEngine = 'mssql' | 'pgsql' | 'mysql' | 'oracle' | 'sqlite';

/**
 * Which kind of introspection payload was clipped. Deliberately a small
 * closed union: it is both the analytics property (must stay
 * low-cardinality — never names, never counts) and the vocabulary the pill
 * tooltip renders, so a fetcher must map its query onto one of these buckets
 * rather than inventing a label.
 */
export type TruncationArtefact =
  | 'tables'
  | 'columns'
  | 'indexes'
  | 'foreignKeys'
  | 'checks'
  | 'routines'
  | 'sequences'
  | 'types';

/**
 * Canonical render order — the tooltip lists artefacts in this order rather
 * than in the (parallel, non-deterministic) order the fetchers happened to
 * report them, so the same clip always produces the same sentence.
 */
const ARTEFACT_ORDER: readonly TruncationArtefact[] = [
  'tables',
  'columns',
  'indexes',
  'foreignKeys',
  'checks',
  'routines',
  'sequences',
  'types',
];

const ARTEFACT_LABELS: Record<TruncationArtefact, string> = {
  tables: 'tables',
  columns: 'columns',
  indexes: 'indexes',
  foreignKeys: 'foreign keys',
  checks: 'check constraints',
  routines: 'routines',
  sequences: 'sequences',
  types: 'user types',
};

export type ReportIntrospectionTruncationParams = {
  connectionId: string;
  database: string;
  engine: IntrospectionTruncationEngine;
  /**
   * The `maxRows` cap of the fetch that tripped. Informational only since the
   * toast (which named it) is gone: the pill copy talks about "the cap"
   * rather than a number, because several differently-capped fetchers can
   * trip in one snapshot build and a single number would be a half-truth.
   * Kept on the params so callers keep reporting it (and a future diagnostic
   * surface can use it) — never sent to analytics.
   */
  cap: number;
  /**
   * Which payload was clipped. Optional so a fetcher that has not been
   * updated yet still compiles and still turns the pill amber — an
   * artefact-less report is recorded as "something in the schema list was
   * clipped" and the tooltip says exactly that.
   */
  artefact?: TruncationArtefact;
};

type TruncationRecord = {
  /** Artefacts named by their fetcher. */
  readonly artefacts: Set<TruncationArtefact>;
  /** True once a fetcher reported a clip without naming its artefact. */
  unnamed: boolean;
  /**
   * Artefacts whose fetch FAILED (connection lost, cancelled, permission
   * denied) rather than clipped. Same pill surface — `partial`, with a
   * "Reload schema" action — different sentence (release review 1.5.0, F3).
   */
  readonly failed: Set<TruncationArtefact>;
  /** The engine's error message for each failed artefact, when the fetcher had one. */
  readonly failureMessages: Map<TruncationArtefact, string>;
};

function recordFor(key: string): TruncationRecord {
  const existing = truncatedPairs.get(key);
  if (existing) return existing;
  const created: TruncationRecord = {
    artefacts: new Set(),
    unnamed: false,
    failed: new Set(),
    failureMessages: new Map(),
  };
  truncatedPairs.set(key, created);
  return created;
}

/** `${connectionId}::${database}` → what was clipped, for live records only. */
const truncatedPairs = new Map<string, TruncationRecord>();

/**
 * `${connectionId}::${database}::${artefact}` keys already counted. Session-
 * scoped and deliberately NOT cleared by `clearTruncation` / bus
 * invalidation: the counter answers "how many users hit this cap at all",
 * so a re-load that trips the same artefact again must not double-count it.
 */
const analyticsReported = new Set<string>();

/** Live schema-bus subscriptions, one per connection that has tripped. */
const busSubscriptions = new Map<string, () => void>();

/**
 * Listeners told whenever the recorded state for a `(connectionId, database)`
 * changes — a clip or failure recorded, or a record cleared. The language-
 * service providers subscribe once and re-derive the status of every document
 * on that scope, which is how a phase-2 failure that lands AFTER the
 * document went `ready` still reaches the pill (release review 1.5.0, R2).
 * The `database` argument is the scope as reported (not lowercased).
 */
export type SchemaReadyStatusListener = (connectionId: string, database: string) => void;
const statusListeners = new Set<SchemaReadyStatusListener>();

export function subscribeSchemaReadyStatus(listener: SchemaReadyStatusListener): () => void {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

function notifyStatusChanged(connectionId: string, database: string | undefined): void {
  for (const listener of statusListeners) {
    try {
      listener(connectionId, database ?? '');
    } catch {
      // A listener must never break the recording path.
    }
  }
}

// mssql database names are case-insensitive server-side, and a fetcher's
// `database` can arrive differently cased than the document's registered one
// (connection default vs. a `USE` warm) — fold case so a report and the
// pill's lookup can never miss each other (same rationale as `sameDatabase`
// in mssqlLanguageServiceProvider.ts).
function pairKey(connectionId: string, database: string | undefined): string {
  return `${connectionId}::${(database ?? '').toLowerCase()}`;
}

function ensureBusSubscription(connectionId: string): void {
  if (busSubscriptions.has(connectionId)) return;
  // `invalidated` (post-DDL) and `disconnected` both mean the snapshot this
  // record describes is gone, so the record must go with it — otherwise a
  // post-DDL rebuild that fits under the cap would leave the pill amber
  // forever. The bus iterates a copy of its listener set, so unsubscribing
  // from inside the callback (via `clearTruncation`) is safe.
  const unsubscribe = subscribeSchemaBus(connectionId, (event) => {
    if (event.type === 'invalidated' || event.type === 'disconnected') {
      clearTruncation(connectionId);
    }
  });
  busSubscriptions.set(connectionId, unsubscribe);
}

/**
 * Report that a schema-introspection query for `(connectionId, database)` hit
 * its row cap. Records the artefact (so the pill can name it) and fires the
 * analytics counter at most once per `(connectionId, database, artefact)` per
 * session, no matter how many of the parallel fetchers in a snapshot build
 * report the same clip. Shows no toast — the pill is the surface.
 */
export function reportIntrospectionTruncation(params: ReportIntrospectionTruncationParams): void {
  const { connectionId, database, engine, artefact } = params;
  const key = pairKey(connectionId, database);

  const record = recordFor(key);
  if (artefact) record.artefacts.add(artefact);
  else record.unnamed = true;

  ensureBusSubscription(connectionId);
  notifyStatusChanged(connectionId, database);

  const analyticsKey = `${key}::${artefact ?? 'unnamed'}`;
  if (analyticsReported.has(analyticsKey)) return;
  analyticsReported.add(analyticsKey);

  if (typeof window !== 'undefined' && window.electronAPI?.analytics?.track) {
    // `artefact` is omitted rather than sent as "unknown" when the fetcher
    // didn't name it — keeps the property's value set closed.
    const properties: Record<string, string> = artefact ? { engine, artefact } : { engine };
    window.electronAPI.analytics
      .track('feature:intellisense_snapshot_truncated', properties)
      .catch(() => {
        // Analytics must never affect UX.
      });
  }
}

/**
 * Report that a schema-introspection query for `(connectionId, database)`
 * FAILED outright. The snapshot keeps whatever pages landed before (and the
 * previous refresh's carried-over metadata), the pill goes `partial` naming
 * the artefact, and "Reload schema" retries. No analytics here: the cache
 * that owns the load fires `feature:schema_snapshot_partial` once per build.
 */
export function reportIntrospectionFailure(params: {
  connectionId: string;
  database: string;
  artefact: TruncationArtefact;
  /** The engine's error message, kept so a surface can say WHY the load failed. */
  message?: string;
}): void {
  const record = recordFor(pairKey(params.connectionId, params.database));
  record.failed.add(params.artefact);
  if (params.message && !record.failureMessages.has(params.artefact)) {
    record.failureMessages.set(params.artefact, params.message);
  }
  ensureBusSubscription(params.connectionId);
  notifyStatusChanged(params.connectionId, params.database);
}

/**
 * A fresh schema load for `(connectionId, database)` is starting. Forget the
 * clips and failures recorded by the PREVIOUS load of that scope — the same
 * thing the pill's "Reload schema" does explicitly — so the record only ever
 * describes the load that is current. Without this a successful retry after
 * the degraded-cache window kept the previous "could not be loaded" warning
 * until a manual reload (release review 1.5.0, R3). Only this scope is
 * touched, so another database's load in flight keeps its own record.
 */
export function beginIntrospectionLoad(connectionId: string, database: string): void {
  const key = pairKey(connectionId, database);
  if (!truncatedPairs.has(key)) return;
  truncatedPairs.delete(key);
  notifyStatusChanged(connectionId, database);
}

/**
 * A load of `(connectionId, database)` finished with nothing missing. Clear
 * any failure a superseded load of the same scope may have recorded after
 * `beginIntrospectionLoad` ran; clips recorded by THIS load stay.
 */
export function clearIntrospectionFailures(connectionId: string, database: string): void {
  const key = pairKey(connectionId, database);
  const record = truncatedPairs.get(key);
  if (!record || record.failed.size === 0) return;
  record.failed.clear();
  record.failureMessages.clear();
  if (record.artefacts.size === 0 && !record.unnamed) truncatedPairs.delete(key);
  notifyStatusChanged(connectionId, database);
}

/** The artefacts whose fetch failed for `(connectionId, database)`, in canonical order. */
export function getFailedArtefacts(
  connectionId: string,
  database: string | undefined,
): readonly TruncationArtefact[] {
  const record = truncatedPairs.get(pairKey(connectionId, database));
  if (!record) return [];
  return ARTEFACT_ORDER.filter((artefact) => record.failed.has(artefact));
}

/**
 * The engine's error message for a failed `artefact` of `(connectionId,
 * database)` — `undefined` when that artefact did not fail, or failed without
 * a message.
 */
export function getIntrospectionFailureMessage(
  connectionId: string,
  database: string | undefined,
  artefact: TruncationArtefact,
): string | undefined {
  return truncatedPairs.get(pairKey(connectionId, database))?.failureMessages.get(artefact);
}

/**
 * The artefacts recorded as clipped for `(connectionId, database)`, in
 * canonical order. Empty when nothing was clipped — and also when the only
 * report so far did not name its artefact, so callers deciding "is the
 * snapshot partial?" must use `isSchemaTruncated` /
 * `resolveSchemaReadyStatus` rather than this array's length.
 */
export function getTruncatedArtefacts(
  connectionId: string,
  database: string | undefined,
): readonly TruncationArtefact[] {
  const record = truncatedPairs.get(pairKey(connectionId, database));
  if (!record) return [];
  return ARTEFACT_ORDER.filter((artefact) => record.artefacts.has(artefact));
}

/** True while any introspection clip stands for `(connectionId, database)`. */
export function isSchemaTruncated(
  connectionId: string | undefined,
  database: string | undefined,
): boolean {
  if (!connectionId) return false;
  const record = truncatedPairs.get(pairKey(connectionId, database));
  if (!record) return false;
  return record.artefacts.size > 0 || record.unnamed || record.failed.size > 0;
}

/**
 * The `message` the `partial` status payload carries. Clipped artefacts and
 * failed artefacts get their own clause each; a failure sentence always names
 * the retry action, because unlike a clip it goes away on its own only if
 * the user reloads (or the degraded snapshot's short retry window elapses).
 */
export function buildTruncationMessage(
  artefacts: readonly TruncationArtefact[],
  failed: readonly TruncationArtefact[] = [],
): string {
  const clauses: string[] = [];
  if (failed.length > 0) {
    const subject = formatArtefactList(failed.map((artefact) => ARTEFACT_LABELS[artefact]));
    clauses.push(`${subject} could not be loaded; use Reload schema to retry`);
  }
  if (artefacts.length > 0 || failed.length === 0) {
    const subject =
      artefacts.length === 0
        ? 'the schema list'
        : formatArtefactList(artefacts.map((artefact) => ARTEFACT_LABELS[artefact]));
    clauses.push(`${subject} exceeded the cap`);
  }
  return `Schema partially loaded — ${clauses.join('; ')}; some completions may be missing.`;
}

function formatArtefactList(labels: readonly string[]): string {
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * What a language-service provider should emit where it would otherwise emit
 * `ready`: schema-aware completion IS live, so the pill never regresses to
 * `initializing`/`error` — it goes `partial`, carrying the human message that
 * names the clipped artefacts.
 */
export function resolveSchemaReadyStatus(
  connectionId: string | undefined,
  database: string | undefined,
): { status: 'ready' | 'partial'; message?: string } {
  if (!isSchemaTruncated(connectionId, database)) return { status: 'ready' };
  return {
    status: 'partial',
    message: buildTruncationMessage(
      getTruncatedArtefacts(connectionId!, database),
      getFailedArtefacts(connectionId!, database),
    ),
  };
}

/**
 * Forget the recorded clips for a connection (all databases) or for one
 * `(connectionId, database)` pair. Called by the schema bus' invalidation
 * events and by the pill's "Reload schema" action immediately before it
 * forces a refresh, so the refetch decides the pill state from scratch.
 */
export function clearTruncation(connectionId: string, database?: string): void {
  if (database === undefined) {
    const prefix = `${connectionId}::`;
    const cleared: string[] = [];
    for (const key of Array.from(truncatedPairs.keys())) {
      if (!key.startsWith(prefix)) continue;
      truncatedPairs.delete(key);
      cleared.push(key.slice(prefix.length));
    }
    // Drop the subscription too: `invalidateConnection` already removed every
    // listener for this connection, and the next report re-subscribes.
    busSubscriptions.get(connectionId)?.();
    busSubscriptions.delete(connectionId);
    for (const db of cleared) notifyStatusChanged(connectionId, db);
    return;
  }
  if (truncatedPairs.delete(pairKey(connectionId, database))) {
    notifyStatusChanged(connectionId, database);
  }
}

/** Test-only: wipe every per-session record between test cases. */
export function __resetIntrospectionTruncationForTest(): void {
  truncatedPairs.clear();
  analyticsReported.clear();
  for (const unsubscribe of busSubscriptions.values()) unsubscribe();
  busSubscriptions.clear();
}
